
import React, { useState, useRef } from "react";
import { Rule } from "@/lib/types";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { RuleForm, RuleFormRef, formSchema, FormValues } from "./RuleForm";
import { createRule, updateRule } from "@/services/ruleService";
import { useIsMobile } from "@/hooks/use-mobile";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
  SheetTrigger,
} from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";

interface RuleDrawerProps {
  tenantId: string;
  mailboxId: string;
  rule?: Rule | null;
  mode: 'create' | 'edit';
  onSuccess: () => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  triggerButton?: boolean;
}

const RuleDrawer: React.FC<RuleDrawerProps> = ({
  tenantId,
  mailboxId,
  rule = null,
  mode = 'create',
  onSuccess,
  open: controlledOpen,
  onOpenChange: setControlledOpen,
  triggerButton = true
}) => {
  const isMobile = useIsMobile();
  const [internalOpen, setInternalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const ruleFormRef = useRef<RuleFormRef>(null);
  
  const [conditions, setConditions] = useState(rule?.conditions || []);
  const [actions, setActions] = useState(rule?.actions || []);
  const [isFormValid, setIsFormValid] = useState(false);
  
  const isControlled = controlledOpen !== undefined && setControlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = isControlled ? setControlledOpen : setInternalOpen;

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: rule?.name || "",
      enabled: rule?.enabled !== undefined ? rule.enabled : true,
    }
  });

  const onFormSubmit = async (formData: FormValues) => {
    setIsSubmitting(true);
    
    try {
      const ruleData: Omit<Rule, "id" | "created_at" | "updated_at"> = {
        tenant_id: tenantId,
        mailbox_id: mailboxId,
        name: formData.name,
        order: rule?.order || 0, // For edit mode, keep existing order; for create mode, this will be overridden by the service
        enabled: formData.enabled,
        conditions: conditions,
        actions: actions
      };
      
      let success = false;
      
      if (mode === 'create') {
        const createdRule = await createRule(ruleData);
        success = !!createdRule;
        if (success) {
          toast({
            title: "Rule created",
            description: "Your rule has been created successfully."
          });
        }
      } else if (rule) {
        const updatedRule = await updateRule({
          id: rule.id,
          ...ruleData
        });
        success = !!updatedRule;
        if (success) {
          toast({
            title: "Rule updated",
            description: "Your rule has been updated successfully."
          });
        }
      }
      
      if (success) {
        setOpen(false);
        // Call onSuccess after successful operation
        onSuccess();
      }
    } catch (error) {
      toast({
        title: "Error",
        description: `Failed to ${mode === 'create' ? 'create' : 'update'} rule. Please try again.`,
        variant: "destructive"
      });
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitClick = () => {
    // Use ref to validate and show errors
    if (ruleFormRef.current?.validateAndAttemptSubmit()) {
      // If validation passes, submit the form
      form.handleSubmit(onFormSubmit)();
    }
    // If validation fails, errors will be shown but submit won't happen
  };

  const handleCancel = () => {
    setOpen(false);
  };

  const sheetWidthClass = isMobile ? "w-full" : "w-full lg:w-4/5 xl:w-3/5 2xl:w-2/5";

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      {triggerButton && mode === 'create' && (
        <SheetTrigger asChild>
          <Button size="sm" variant="outline" className="flex items-center gap-1">
            <Plus className="h-3 w-3" /> Add Rule
          </Button>
        </SheetTrigger>
      )}
      <SheetContent
        side="right"
        className={`p-0 rounded-none border-l shadow-lg ${sheetWidthClass} flex flex-col h-full`}
        style={{ transform: "translateX(0)" }}
      >
        <SheetHeader className="p-6 border-b">
          <SheetTitle>{mode === 'create' ? 'Add New Rule' : 'Edit Rule'}</SheetTitle>
          <SheetDescription>
            {mode === 'create' 
              ? 'Create a new rule for automated email processing.' 
              : 'Update your email processing rule.'}
          </SheetDescription>
        </SheetHeader>
        
        <ScrollArea className="flex-grow px-6 py-4">
          <RuleForm
            ref={ruleFormRef}
            initialData={rule || undefined}
            tenantId={tenantId}
            mailboxId={mailboxId}
            form={form}
            conditions={conditions}
            onConditionsChange={setConditions}
            actions={actions}
            onActionsChange={setActions}
            onValidationChange={setIsFormValid}
          />
        </ScrollArea>

        <SheetFooter className="border-t p-6">
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button 
              type="button"
              disabled={isSubmitting}
              onClick={handleSubmitClick}
            >
              {isSubmitting ? "Saving..." : mode === 'create' ? "Create Rule" : "Update Rule"}
            </Button>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

export default RuleDrawer;
